import React, { useState, useEffect, useRef, useCallback } from 'react';

const StudyTimer = ({ onSessionComplete, selectedTask }) => {
  const [time, setTime] = useState(25 * 60); // 25 minutes in seconds
  const [isRunning, setIsRunning] = useState(false);
  const [subject, setSubject] = useState('');
  const [customTime, setCustomTime] = useState(25);
  const intervalRef = useRef(null);

  const handleSessionComplete = useCallback(() => {
    setIsRunning(false);
    const session = {
      id: Date.now(),
      subject: subject || 'General Study',
      duration: customTime * 60,
      completedAt: new Date().toISOString(),
      date: new Date().toLocaleDateString()
    };
    onSessionComplete(session);

    // Play completion sound
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmEcBSqJ2fPFdywGII7E8N2QQAoUXrTp66hVFApGn+DyvmEcBSuJ2fPCdywGII7E8N2QQAoUXrTp66hVFApGn+DyvmEcBSuJ2fPCdywGII7E8N2QQAoUXrTp66hVFApGn+DyvmEcBSuJ2fPCdywGII7E8N2QQAoUXrTp66hVFApGn+DyvmEcBSuJ2fPCdywGII7E8N2QQAoUXrTp66hVFApGn+DyvmEcBSuJ2fPCdywGII7E8N2QQAoUXrTp66hVFA==');
    audio.play().catch(() => {}); // Ignore errors if audio can't play

    // Reset timer
    setTime(customTime * 60);
    setSubject('');
  }, [customTime, subject, onSessionComplete]);

  useEffect(() => {
    if (isRunning && time > 0) {
      intervalRef.current = setInterval(() => {
        setTime(prevTime => prevTime - 1);
      }, 1000);
    } else if (time === 0) {
      handleSessionComplete();
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isRunning, time, handleSessionComplete]);

  const startTimer = () => {
    if (time > 0) {
      setIsRunning(true);
    }
  };

  const pauseTimer = () => {
    setIsRunning(false);
  };

  const resetTimer = () => {
    setIsRunning(false);
    setTime(customTime * 60);
  };

  const setPresetTime = (minutes) => {
    if (!isRunning) {
      setCustomTime(minutes);
      setTime(minutes * 60);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = () => {
    const totalTime = customTime * 60;
    return ((totalTime - time) / totalTime) * 100;
  };

  return (
    <div className="study-timer">
      <div className="timer-container">
        <div className="timer-display">
          <div className="progress-ring">
            <svg width="240" height="240">
              <circle
                cx="120"
                cy="120"
                r="100"
                stroke="#e5e7eb"
                strokeWidth="8"
                fill="transparent"
              />
              <circle
                cx="120"
                cy="120"
                r="100"
                stroke="url(#gradient)"
                strokeWidth="8"
                fill="transparent"
                strokeDasharray={`${2 * Math.PI * 100}`}
                strokeDashoffset={`${2 * Math.PI * 100 * (1 - getProgressPercentage() / 100)}`}
                transform="rotate(-90 120 120)"
                className="progress-circle"
                strokeLinecap="round"
              />
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#4f46e5" />
                  <stop offset="100%" stopColor="#6366f1" />
                </linearGradient>
              </defs>
            </svg>
            <div className="time-text">{formatTime(time)}</div>
          </div>
        </div>

        <div className="timer-controls">
          <div className="preset-buttons">
            <button
              onClick={() => setPresetTime(25)}
              className={`btn ${customTime === 25 ? 'btn-primary' : 'btn-secondary'}`}
              disabled={isRunning}
            >
              25m
            </button>
            <button
              onClick={() => setPresetTime(45)}
              className={`btn ${customTime === 45 ? 'btn-primary' : 'btn-secondary'}`}
              disabled={isRunning}
            >
              45m
            </button>
            <button
              onClick={() => setPresetTime(60)}
              className={`btn ${customTime === 60 ? 'btn-primary' : 'btn-secondary'}`}
              disabled={isRunning}
            >
              60m
            </button>
          </div>

          <div className="form-group">
            <label className="form-label">Custom time (minutes):</label>
            <input
              type="number"
              min="1"
              max="180"
              value={customTime}
              onChange={(e) => {
                const newTime = parseInt(e.target.value) || 1;
                setCustomTime(newTime);
                if (!isRunning) {
                  setTime(newTime * 60);
                }
              }}
              disabled={isRunning}
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Subject (optional):</label>
            <input
              type="text"
              placeholder="e.g., Mathematics, History..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={isRunning}
              className="form-control"
            />
          </div>

          <div className="action-buttons">
            {!isRunning ? (
              <button
                onClick={startTimer}
                className="btn btn-primary btn-lg"
                disabled={time === 0}
              >
                ▶ Start Session
              </button>
            ) : (
              <button
                onClick={pauseTimer}
                className="btn btn-warning btn-lg"
              >
                ⏸ Pause
              </button>
            )}
            <button
              onClick={resetTimer}
              className="btn btn-secondary"
            >
              🔄 Reset
            </button>
          </div>
        </div>

        {selectedTask && (
          <div className="card" style={{
            background: '#f0fdf4',
            border: '1px solid #10b981',
            marginTop: '1rem'
          }}>
            <h4 style={{ color: '#059669', marginBottom: '0.5rem' }}>
              📝 Currently working on:
            </h4>
            <div>
              <strong style={{ color: '#065f46' }}>
                {selectedTask.title}
              </strong>
              {selectedTask.subject && (
                <span style={{
                  background: '#10b981',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  marginLeft: '0.5rem'
                }}>
                  {selectedTask.subject}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudyTimer;